import React, { useEffect, useState, useRef } from 'react';
import { Activity, X, Cpu, MemoryStick, HardDrive, Clock, Loader2, AlertTriangle, CheckCircle2, Database } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import axios from 'axios';

// URL relative : reste sur le même domaine que le frontend, évite les soucis CORS
// quand le REACT_APP_BACKEND_URL au build ne correspond pas au domaine de prod.
const API = '/api';

const formatUptime = (seconds) => {
  if (!seconds || seconds < 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}j ${h}h ${m}min`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m} min`;
};

const Bar = ({ percent, label, used, total, unit, icon: Icon, sublabel }) => {
  // Couleur selon palier
  const isWarn = percent >= 70 && percent < 85;
  const isCrit = percent >= 85;
  const barColor = isCrit ? 'bg-red-500' : isWarn ? 'bg-amber-500' : 'bg-emerald-500';
  const textColor = isCrit ? 'text-red-400' : isWarn ? 'text-amber-300' : 'text-emerald-400';
  const ringColor = isCrit ? 'ring-red-500/30' : isWarn ? 'ring-amber-500/30' : 'ring-emerald-500/20';

  return (
    <div className={`rounded-xl bg-zinc-900/60 border border-zinc-700 p-4 ring-1 ${ringColor}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm text-zinc-300 font-semibold">
          <Icon className={`h-4 w-4 ${textColor}`} />
          {label}
        </div>
        <div className={`text-2xl font-bold ${textColor} tabular-nums`}>
          {percent.toFixed(1)}<span className="text-base font-medium opacity-70">%</span>
        </div>
      </div>
      <div className="w-full h-2 rounded-full bg-zinc-800 overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-500 ease-out`}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
      {(used != null && total != null) && (
        <div className="mt-2 text-xs text-zinc-500 tabular-nums">
          <span className="text-zinc-300 font-medium">{used}</span> / {total} {unit}
        </div>
      )}
      {sublabel && (
        <div className="mt-1 text-xs text-zinc-500">{sublabel}</div>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    healthy: { label: 'EN BONNE SANTÉ', icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/15 border-emerald-500/40' },
    warning: { label: 'SURVEILLANCE', icon: AlertTriangle, color: 'text-amber-300', bg: 'bg-amber-500/15 border-amber-500/40' },
    critical: { label: 'CRITIQUE', icon: AlertTriangle, color: 'text-red-400 animate-pulse', bg: 'bg-red-500/15 border-red-500/40' },
  };
  const c = map[status] || map.healthy;
  const Icon = c.icon;
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${c.bg} ${c.color} text-xs font-bold tracking-wider`} data-testid="health-status-badge">
      <Icon className="h-3.5 w-3.5" />
      {c.label}
    </div>
  );
};

const SystemHealthModal = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchHealth = async () => {
    try {
      const res = await axios.get(`${API}/system/health`);
      setData(res.data);
      setError(null);
    } catch (e) {
      // Détails techniques pour aider au diagnostic
      const status = e.response?.status ? `[HTTP ${e.response.status}]` : '[Réseau]';
      const url = e.config?.url || `${API}/system/health`;
      const msg = e.response?.data?.detail || e.message || 'Impossible de récupérer les métriques';
      setError(`${status} ${msg}\nURL appelée : ${url}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchHealth();
    // ⚡ Auto-refresh toutes les 5 secondes (assez réactif sans surcharger).
    // Le polling s'arrête automatiquement quand le modal est fermé.
    intervalRef.current = setInterval(fetchHealth, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-zinc-950 border-[#C4A052]/40 max-w-3xl text-white" data-testid="system-health-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl font-bold text-white">
            <Activity className="h-5 w-5 text-[#C4A052]" />
            État du serveur en temps réel
            {data && <StatusBadge status={data.status} />}
          </DialogTitle>
        </DialogHeader>

        {loading && !data && (
          <div className="flex flex-col items-center justify-center py-12 text-zinc-400">
            <Loader2 className="h-8 w-8 animate-spin text-[#C4A052] mb-2" />
            <p className="text-sm">Mesure des métriques...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-300 text-sm whitespace-pre-line font-mono" data-testid="health-error">
            {error}
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Bar
                percent={data.cpu.percent}
                label={`CPU (${data.cpu.count} ${data.cpu.count === 1 ? 'cœur' : 'cœurs'}${data.cpu.is_container_limit ? ' alloués' : ''})`}
                icon={Cpu}
                sublabel={
                  data.cpu.is_container_limit && data.cpu.host_count
                    ? `Quota conteneur · Hôte : ${data.cpu.host_count} cœurs · Load avg : ${data.cpu.load_avg.join(' · ')}`
                    : `Load avg : ${data.cpu.load_avg.join(' · ')}`
                }
              />
              <Bar
                percent={data.memory.percent}
                label={`Mémoire RAM${data.memory.is_container_limit ? ' (conteneur)' : ''}`}
                used={data.memory.used_gb.toFixed(1)}
                total={data.memory.total_gb.toFixed(1)}
                unit="Go"
                icon={MemoryStick}
              />
              <Bar
                percent={data.disk.percent}
                label="Disque"
                used={data.disk.used_gb.toFixed(0)}
                total={data.disk.total_gb.toFixed(0)}
                unit="Go"
                icon={HardDrive}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
              <div className="rounded-lg bg-zinc-900/60 border border-zinc-700 p-3">
                <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider">
                  <Clock className="h-3 w-3" /> Uptime
                </div>
                <div className="mt-1 text-lg font-bold text-white tabular-nums">{formatUptime(data.uptime_seconds)}</div>
              </div>
              <div className="rounded-lg bg-zinc-900/60 border border-zinc-700 p-3">
                <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider">
                  <Database className="h-3 w-3" /> Tickets en base
                </div>
                <div className="mt-1 text-lg font-bold text-white tabular-nums">{data.tickets_count.toLocaleString('fr-FR')}</div>
              </div>
              <div className="rounded-lg bg-zinc-900/60 border border-zinc-700 p-3">
                <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider">
                  <Activity className="h-3 w-3" /> Refresh
                </div>
                <div className="mt-1 text-lg font-bold text-emerald-400 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Toutes les 5 s
                </div>
              </div>
            </div>

            {/* Légende seuils */}
            <div className="mt-4 text-xs text-zinc-500 flex items-center gap-4 flex-wrap">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> &lt; 70 % · OK</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> 70-85 % · Surveillance</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> &gt; 85 % · Saturation</span>
            </div>
          </>
        )}

        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="close-system-health">
            <X className="h-4 w-4 mr-1.5" /> Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SystemHealthModal;
